import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getEvent, updateEventTravel } from '../utils/api';
import MeetingScheduler from '../components/MeetingScheduler';

function CategoryBadge({ category }) {
  const map = {
    lp_cogp: { label: 'LP/Co-GP', cls: 'badge-green' },
    lender: { label: 'Lender', cls: 'badge-blue' },
    broker: { label: 'Broker', cls: 'badge-purple' },
    partner_service: { label: 'Partner', cls: 'badge-amber' },
    seller_operator: { label: 'Seller/Op', cls: 'badge-teal' },
  };
  const { label, cls } = map[category] || { label: category, cls: 'badge-neutral' };
  return <span className={`badge ${cls}`}>{label}</span>;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateRange(start, end) {
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  const opts = { month: 'long', day: 'numeric' };
  if (start === end) return s.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
  if (s.getFullYear() === e.getFullYear()) {
    return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
  }
  return `${s.toLocaleDateString('en-US', { ...opts, year: 'numeric' })} – ${e.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
}

function daysAway(dateStr) {
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(dateStr + 'T00:00:00');
  return Math.round((d - today) / 86400000);
}

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [travelEditing, setTravelEditing] = useState(null); // memberId being edited
  const [travelForm, setTravelForm] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    getEvent(id)
      .then(data => {
        setEvent(data);
        setContacts(data.contacts || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleMeetingUpdate = (contactId, updated) => {
    setContacts(prev => prev.map(c =>
      c.id === contactId ? { ...c, ...(updated || {
        meeting_date: null, meeting_time: null,
        meeting_duration: 30, meeting_location: null, meeting_synced: false
      }) } : c
    ));
  };

  const handleTravelSave = async (memberId) => {
    try {
      await updateEventTravel(id, memberId, travelForm[memberId] || {});
      setTravelEditing(null);
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  if (loading) return <div className="page-loading">Loading event…</div>;
  if (error) return <div className="page-error">{error}</div>;
  if (!event) return null;

  const da = daysAway(event.start_date);
  const isPast = new Date(event.end_date + 'T00:00:00') < new Date();
  const confirmedMeetings = contacts.filter(c => c.meeting_date && c.meeting_time).length;

  return (
    <div className="page-container">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/events">Events</Link> <span>/</span> <span>{event.name}</span>
      </div>

      {/* Header */}
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
            <h1 className="page-title" style={{ margin: 0 }}>{event.name}</h1>
            {!isPast && (
              <span className={`badge ${da <= 7 ? 'badge-amber' : 'badge-neutral'}`}>
                {da === 0 ? 'Today' : da === 1 ? 'Tomorrow' : da < 0 ? 'Active' : `${da} days away`}
              </span>
            )}
            {isPast && <span className="badge badge-neutral">Past</span>}
          </div>
          <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
            {formatDateRange(event.start_date, event.end_date)}
            {event.venue && ` · ${event.venue}`}
            {event.city && ` · ${event.city}${event.state ? `, ${event.state}` : ''}`}
          </div>
        </div>
        <Link to={`/events/${id}/edit`} className="btn btn-secondary">Edit Event</Link>
      </div>

      {/* Attendees */}
      {event.attendees && event.attendees.length > 0 && (
        <div className="detail-section">
          <div className="detail-section-label">Team Attending</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {event.attendees.map(a => (
              <span key={a.id} className="badge badge-neutral">{a.display_name}</span>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      {event.notes && (
        <div className="detail-section">
          <div className="detail-section-label">Notes / Agenda</div>
          <div className="notes-text">{event.notes}</div>
        </div>
      )}

      {/* Pre-event prep — contact cards */}
      <div className="detail-section">
        <div className="detail-section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Pre-Event Prep ({contacts.length} contacts)</span>
          {confirmedMeetings > 0 && (
            <span style={{ color: 'var(--color-success)', fontSize: '0.8rem' }}>
              {confirmedMeetings} meeting{confirmedMeetings !== 1 ? 's' : ''} scheduled
            </span>
          )}
        </div>
        {contacts.length === 0 ? (
          <div className="empty-state-inline">No contacts linked to this event.</div>
        ) : (
          <div className="prep-card-grid">
            {contacts.map(c => {
              const hasMeeting = !!(c.meeting_date && c.meeting_time);
              return (
                <div key={c.id} className={`prep-card ${hasMeeting ? 'has-meeting' : ''}`}>
                  <div className="prep-card-header">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div className={`contact-avatar avatar-${c.category}`}>
                        {c.first_name?.[0]}{c.last_name?.[0]}
                      </div>
                      <div>
                        <Link to={`/contacts/${c.id}`} className="prep-card-name">
                          {c.first_name} {c.last_name}
                        </Link>
                        {c.organization && <div className="prep-card-org">{c.organization}</div>}
                        {c.title && <div className="prep-card-title">{c.title}</div>}
                      </div>
                    </div>
                    <CategoryBadge category={c.category} />
                  </div>

                  {c.owner_name && (
                    <div className="prep-card-owner">Owner: {c.owner_name}</div>
                  )}

                  {c.last_interaction_summary && (
                    <div className="prep-card-last-interaction">
                      <span style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
                        Last: {c.last_interaction_date ? formatDate(c.last_interaction_date.split('T')[0]) : ''}
                      </span>
                      <div className="prep-card-summary">{c.last_interaction_summary}</div>
                    </div>
                  )}

                  <div className="prep-card-meeting">
                    <MeetingScheduler
                      eventId={id}
                      contactId={c.id}
                      meeting={c}
                      venue={event.venue}
                      readOnly={isPast}
                      onUpdate={(updated) => handleMeetingUpdate(c.id, updated)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Travel details */}
      {event.attendees && event.attendees.length > 0 && (
        <div className="detail-section">
          <div className="detail-section-label">Travel Details</div>
          <div className="travel-grid">
            {event.attendees.map(a => {
              const travelRecord = (event.travel || []).find(t => t.member_id === a.id) || {};
              const isEditing = travelEditing === a.id;

              return (
                <div key={a.id} className="travel-card">
                  <div className="travel-card-header">
                    <strong>{a.display_name}</strong>
                    {!isPast && (
                      <button
                        className="btn-link-sm"
                        onClick={() => {
                          if (isEditing) {
                            setTravelEditing(null);
                          } else {
                            setTravelForm(f => ({
                              ...f,
                              [a.id]: {
                                flight_out: travelRecord.flight_out || '',
                                flight_back: travelRecord.flight_back || '',
                                hotel: travelRecord.hotel || '',
                              }
                            }));
                            setTravelEditing(a.id);
                          }
                        }}
                      >
                        {isEditing ? 'Cancel' : 'Edit'}
                      </button>
                    )}
                  </div>
                  {isEditing ? (
                    <div>
                      {['flight_out', 'flight_back', 'hotel'].map(field => (
                        <div key={field} style={{ marginBottom: 6 }}>
                          <label className="form-label-sm">
                            {field === 'flight_out' ? 'Flight Out' : field === 'flight_back' ? 'Flight Back' : 'Hotel'}
                          </label>
                          <input
                            type="text"
                            className="input-sm"
                            style={{ width: '100%', boxSizing: 'border-box' }}
                            value={travelForm[a.id]?.[field] || ''}
                            onChange={e => setTravelForm(f => ({
                              ...f,
                              [a.id]: { ...f[a.id], [field]: e.target.value }
                            }))}
                          />
                        </div>
                      ))}
                      <button className="btn btn-sm btn-primary" onClick={() => handleTravelSave(a.id)}>
                        Save
                      </button>
                    </div>
                  ) : (
                    <div className="travel-details">
                      {travelRecord.flight_out && <div><span className="travel-label">Out:</span> {travelRecord.flight_out}</div>}
                      {travelRecord.flight_back && <div><span className="travel-label">Back:</span> {travelRecord.flight_back}</div>}
                      {travelRecord.hotel && <div><span className="travel-label">Hotel:</span> {travelRecord.hotel}</div>}
                      {!travelRecord.flight_out && !travelRecord.flight_back && !travelRecord.hotel && (
                        <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>No details added</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
